import {
  ApolloError,
  gql,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { useMemo } from 'react';

import { GRAPHQL_TYPENAMES, GraphQLTypeNameData } from 'modules/graphql';
import { useGraphql } from 'utils/hooks/useGraphql';

/**
 * TODO [WF-8277]: Update the frontend to use the goalsCreators and goalsModifiers query
 */
export const fetchGoalsCreatorsAndModifiers = gql`
  fragment userInfo on User {
    id
    fullName
    avatarUrl
  }

  query getWorkflowCreatorsAndModifiers {
    workflowCreators {
      ...userInfo
    }
    workflowModifiers {
      ...userInfo
    }
  }
`;

export type GoalsCreatorOrModifierUser = {
  id: string;
  fullName?: string;
  avatarUrl?: string;
} & GraphQLTypeNameData<GRAPHQL_TYPENAMES.USER>;

export type QueryGoalsCreatorsAndModifiersResponse = {
  workflowCreators: GoalsCreatorOrModifierUser[];
  workflowModifiers: GoalsCreatorOrModifierUser[];
};

export type QueryGoalsCreatorsAndModifiersVariables = OperationVariables;

export type UseQueryGoalsCreatorsAndModifiersState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables: Partial<QueryGoalsCreatorsAndModifiersVariables>) => void;
  goalsCreators: GoalsCreatorOrModifierUser[];
  goalsModifiers: GoalsCreatorOrModifierUser[];
};

export const useQueryGoalsCreatorsAndModifiers = (
  query: TypedDocumentNode<
    QueryGoalsCreatorsAndModifiersResponse,
    QueryGoalsCreatorsAndModifiersVariables
  > = fetchGoalsCreatorsAndModifiers,
  options?: QueryHookOptions<
    QueryGoalsCreatorsAndModifiersResponse,
    QueryGoalsCreatorsAndModifiersVariables
  >,
): UseQueryGoalsCreatorsAndModifiersState => {
  const queryOptions = useMemo(
    () => ({ notifyOnNetworkStatusChange: true, ...options }),
    [options],
  );

  const {
    data,
    errorMessage,
    isLoading,
    error: hasError = false,
    refetch,
  } = useGraphql<QueryGoalsCreatorsAndModifiersResponse, QueryGoalsCreatorsAndModifiersVariables>(
    query,
    queryOptions,
  );

  const { workflowCreators: goalsCreators = [], workflowModifiers: goalsModifiers = [] } =
    data || {};

  return { goalsCreators, goalsModifiers, errorMessage, hasError, isLoading, refetch };
};
